import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import RuleIcon from '@mui/icons-material/Rule';
import { useTranslation } from 'react-i18next';
import { useCRUD } from '../../../../api/crud';
import { JOB_FORM_RULES_ENDPOINT } from '../../../../consts';
import { JobFormRule, JobFormRuleRequiredField } from '../../../../types/jobs';
import { JOB_FORM_RULE_FIELDS } from '../../../../components/service/jobFormRules';
import { useHasPermission } from '../../../../contexts/auth';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import { TENANT_ROUTES } from '../../tenantConfig.routes';
import {
  DetailHeader,
  DetailPage,
  DetailRow,
  DetailSection,
} from '../../../../components/ui/detailView';

export const TenantJobFormRuleDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get } = useCRUD(JOB_FORM_RULES_ENDPOINT);
  const { setOverride, clearOverride } = useBreadcrumbs();
  const [rule, setRule] = useState<JobFormRule | null>(null);
  const canView = useHasPermission('service.view_jobformrule');

  useEffect(() => {
    const listPath = TENANT_ROUTES.jobFormRules;
    setOverride(listPath, t('Job form rules'));

    const fetchRule = async () => {
      if (!id) return;
      const result = await get(id);
      if (result?.success && result?.data) {
        setRule(result.data);
        setOverride(TENANT_ROUTES.jobFormRule(id), `${t('Rule')} #${id}`);
      } else {
        navigate(TENANT_ROUTES.jobFormRules);
      }
    };

    const handleUpdated = () => {
      fetchRule();
    };
    window.addEventListener('jobFormRulesUpdated', handleUpdated);
    fetchRule();
    return () => {
      window.removeEventListener('jobFormRulesUpdated', handleUpdated);
      clearOverride(listPath);
      if (id) {
        clearOverride(TENANT_ROUTES.jobFormRule(id));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  if (!canView) return null;
  if (!rule) return <DetailPage loading />;

  const chipList = (chips: React.ReactNode[], emptyText: string) =>
    chips.length > 0 ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{chips}</Box>
    ) : (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        {emptyText}
      </Typography>
    );

  const renderFieldChip = (field: JobFormRuleRequiredField) => {
    const labelKey = JOB_FORM_RULE_FIELDS[field.field_type]?.labelKey;
    return (
      <Chip
        key={field.id}
        label={labelKey ? t(labelKey) : field.field_type}
        size="small"
        color="warning"
        variant="outlined"
      />
    );
  };

  const taskFields = rule.required_fields.filter((field) => JOB_FORM_RULE_FIELDS[field.field_type]?.onTask);
  const jobFields = rule.required_fields.filter((field) => !JOB_FORM_RULE_FIELDS[field.field_type]?.onTask);

  return (
    <DetailPage>
      <DetailHeader icon={<RuleIcon />} title={`${t('Rule')} #${rule.id}`} />

      <DetailSection title={t('Basic Information')}>
        <DetailRow label={t('When a task has any of these services')}>
          {chipList(
            rule.services.map((service) => (
              <Chip
                key={service.id}
                label={service.title}
                size="small"
                variant="outlined"
                sx={service.color ? { borderColor: service.color } : undefined}
              />
            )),
            t('No services - the rule never applies.')
          )}
        </DetailRow>
        <DetailRow label={t('Require on that task')}>
          {chipList(taskFields.map(renderFieldChip), t('No task fields required.'))}
        </DetailRow>
        <DetailRow label={t('Require on the job')}>
          {chipList(jobFields.map(renderFieldChip), t('No job fields required.'))}
        </DetailRow>
      </DetailSection>
    </DetailPage>
  );
};
